import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { WorkWithSankalp } from "@/components/WorkWithSankalp";
import { InstagramReels } from "@/components/InstagramReels";
import { NumbersBento } from "@/components/founder/NumbersBento";
import { MissionSection } from "@/components/founder/MissionSection";
import { Award, Briefcase, CalendarClock, ExternalLink, GraduationCap, Mail, MapPin, Mic2, Play, Youtube } from "lucide-react";

export default function Founder() {
  const [f, setF] = useState(null);
  const [videos, setVideos] = useState([]);
  const [videosState, setVideosState] = useState("idle");
  useEffect(() => {
    api.get("/catalog/founder").then((r) => setF(r.data)).catch(() => {});
    api.get("/catalog/founder/videos").then((r) => {
      setVideos(r.data.videos || []);
      setVideosState(r.data.channel_id ? (r.data.videos?.length ? "loaded" : "empty") : "unset");
    }).catch(() => setVideosState("error"));
  }, []);
  if (!f) return <div className="min-h-screen bg-[#09090B] text-zinc-400 flex items-center justify-center">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Navbar variant="adult" />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg-adult" />
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-24 grid md:grid-cols-5 gap-10 relative items-center">
          <div className="md:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 font-adult-mono mb-4">Meet Sankalp</div>
            <h1 data-testid="founder-name" className="font-adult-heading text-5xl md:text-7xl font-semibold tracking-tight leading-[0.98]">
              <span className="block">Sankalp Sidhanth</span>
              <span className="block font-adult-serif italic text-amber-400">Misra</span>
            </h1>
            <div className="mt-4 text-lg text-zinc-300">{f.title}</div>
            <div className="mt-2 text-sm text-zinc-500 flex items-center gap-4 flex-wrap font-adult-mono">
              <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5"/>{f.location}</span>
              <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5"/>{f.email}</span>
            </div>
            <p className="mt-8 text-zinc-300 max-w-2xl text-lg leading-relaxed">{f.bio}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link data-testid="founder-cta-1on1" to="/meet-sankalp/1-on-1" className="inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold px-6 py-3 text-sm glow-amber transition-smooth">
                <CalendarClock className="w-4 h-4"/>Book a 1-on-1
              </Link>
              <Link data-testid="founder-cta-invite" to="/invite-sankalp" className="inline-flex items-center gap-2 rounded-full bg-violet-500 hover:bg-violet-400 text-white font-semibold px-6 py-3 text-sm glow-violet transition-smooth">
                <Mic2 className="w-4 h-4"/>Invite Sankalp
              </Link>
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <a href="#work-with-sankalp" data-testid="founder-cta-workshops" className="text-zinc-400 hover:text-amber-400 transition-colors">Live workshops →</a>
              <Link data-testid="founder-cta-flagship" to="/flagship" className="text-zinc-400 hover:text-amber-400 transition-colors">Weekend Bootcamp →</Link>
            </div>
          </div>

          <div className="md:col-span-2 relative">
            <div className="absolute -top-4 -left-4 w-full h-full rounded-3xl border border-amber-500/30 -z-0" />
            <img data-testid="founder-photo" src={f.photo_url} alt="Sankalp Sidhanth Misra" className="relative rounded-3xl border border-zinc-800 w-full aspect-[4/5] object-cover object-top bg-zinc-900" />
            <div className="absolute -bottom-6 -right-4 rounded-2xl bg-zinc-950 border border-zinc-800 px-4 py-3 font-adult-mono text-xs text-zinc-300 glow-violet">
              <span className="text-violet-400">while</span>(learners &lt; 10000) &#123;<br/>
              <span className="text-zinc-500">&nbsp;&nbsp;// keep teaching</span><br/>
              &#125;
            </div>
          </div>
        </div>
      </section>

      <WorkWithSankalp />

      <NumbersBento items={f.achievements} />
      <MissionSection why={f.why} photo={f.photo_url} />

      {/* Career highlights */}
      <section className="max-w-7xl mx-auto px-6 py-6">
        <div className="text-[11px] uppercase tracking-[0.28em] text-zinc-500 font-adult-mono mb-6 code-underline">Career</div>
        <div className="grid md:grid-cols-2 gap-4">
          {f.roles.map((r, i) => (
            <div key={i} data-testid={`role-${i}`} className="rounded-2xl border border-zinc-800 p-6 hover:border-violet-500/40 transition-smooth">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-lg bg-violet-500/10 border border-violet-500/30 flex items-center justify-center text-violet-300 shrink-0">
                  <Briefcase className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs font-adult-mono text-zinc-500 uppercase tracking-[0.2em]">{r.years}</div>
                  <div className="font-adult-heading text-lg mt-1">{r.role}</div>
                  <div className="text-sm text-amber-400 mt-0.5">{r.company}</div>
                  <div className="text-sm text-zinc-400 mt-2 leading-relaxed">{r.note}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Skills + Education */}
      <section className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-3 gap-6">
        <div className="rounded-2xl border border-zinc-800 p-6 md:col-span-2">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-adult-mono text-zinc-500 mb-4">
            <Award className="w-3.5 h-3.5"/>Skills toolbox
          </div>
          <div className="flex flex-wrap gap-2">
            {f.skills.map((s) => (
              <span key={s} className="text-sm px-3 py-1.5 rounded-full border border-amber-500/30 bg-amber-500/5 text-amber-200 font-adult-mono">{s}</span>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-zinc-800 p-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-adult-mono text-zinc-500 mb-4">
            <GraduationCap className="w-3.5 h-3.5"/>Education
          </div>
          {f.education.map((e, i) => (
            <div key={i}>
              <div className="font-adult-heading">{e.degree}</div>
              <div className="text-sm text-zinc-400 mt-1">{e.institution}</div>
              <div className="text-xs font-adult-mono text-zinc-500 mt-1">{e.year}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Reels / Shorts */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 font-adult-mono code-underline">Latest from YouTube</div>
            <h2 className="mt-4 font-adult-heading text-3xl font-semibold tracking-tight">Watch Sankalp teach.</h2>
          </div>
          <div className="hidden md:flex items-center gap-2 text-xs text-zinc-500 font-adult-mono uppercase tracking-[0.2em]">
            <Youtube className="w-3.5 h-3.5 text-red-500"/>Auto-syncs with the channel
          </div>
        </div>

        {videosState === "loaded" && (
          <div className="grid md:grid-cols-3 gap-5" data-testid="founder-videos">
            {videos.slice(0, 3).map((v) => (
              <a key={v.id} href={v.url} target="_blank" rel="noreferrer" data-testid={`video-${v.id}`} className="group rounded-2xl overflow-hidden border border-zinc-800 hover:border-amber-500/60 transition-smooth block">
                <div className="aspect-video relative overflow-hidden bg-zinc-900">
                  <img src={v.thumbnail} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-smooth" style={{transitionDuration:"400ms"}}/>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-black/60 backdrop-blur-md border border-white/20 flex items-center justify-center group-hover:bg-amber-500 group-hover:border-amber-400 transition-smooth">
                      <Play className="w-6 h-6 text-white fill-current" />
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <div className="text-sm text-zinc-200 line-clamp-2 leading-snug">{v.title}</div>
                  <div className="mt-2 text-[10px] text-zinc-500 font-adult-mono uppercase tracking-[0.15em] inline-flex items-center gap-1.5"><ExternalLink className="w-3 h-3"/>Watch on YouTube</div>
                </div>
              </a>
            ))}
          </div>
        )}

        {(videosState === "unset" || videosState === "empty" || videosState === "error") && (
          <div className="rounded-3xl border border-dashed border-zinc-800 p-10 text-center bg-zinc-950/50" data-testid="founder-videos-empty">
            <Youtube className="w-6 h-6 text-red-500 mx-auto" />
            <div className="mt-3 font-adult-heading text-xl">
              {videosState === "unset" && "Waiting for YouTube channel"}
              {videosState === "empty" && "No videos yet"}
              {videosState === "error" && "Couldn't fetch videos right now"}
            </div>
            <p className="mt-2 text-sm text-zinc-400 max-w-lg mx-auto">
              {videosState === "unset"
                ? "Once the admin adds a YouTube channel ID under Admin → Founder settings, the latest videos & shorts appear here automatically."
                : "New uploads on the connected YouTube channel will show up here the next time the page loads."}
            </p>
          </div>
        )}
      </section>

      <InstagramReels reels={f.reels} instagramUrl={f.instagram_url} />

      <Footer variant="adult" />
    </div>
  );
}
